import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { api, ApiError } from '@/lib/api-client';
import { PublicShell } from '@/components/layout/PublicShell';

type TokenStatus = 'checking' | 'valid' | 'invalid' | 'expired' | 'used';

export function ResetPasswordPage() {
  const { token } = useParams({ from: '/reset-password/$token' });
  const navigate = useNavigate();
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>('checking');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<{ email: string; expires_at: number }>(
          `/api/auth/reset-password/${token}`,
        );
        if (cancelled) return;
        setEmail(data.email);
        setTokenStatus('valid');
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.code === 'expired') setTokenStatus('expired');
          else if (err.code === 'already_used') setTokenStatus('used');
          else setTokenStatus('invalid');
        } else {
          setTokenStatus('invalid');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Şifre en az 8 karakter olmalı');
      return;
    }
    if (password !== confirm) {
      setError('Şifreler eşleşmiyor');
      return;
    }
    setSubmitState('submitting');
    try {
      await api.post(`/api/auth/reset-password/${token}`, { password });
      setSubmitState('done');
      setTimeout(() => navigate({ to: '/login' }), 2000);
    } catch (err) {
      setSubmitState('idle');
      setError(err instanceof ApiError ? err.message : 'Bir hata oluştu');
    }
  };

  const statusMsg = {
    expired: 'Bu bağlantının süresi dolmuş. Yeni bir sıfırlama bağlantısı isteyin.',
    used: 'Bu bağlantı daha önce kullanılmış.',
    invalid: 'Bağlantı geçersiz.',
  } as const;

  return (
    <PublicShell>
      <div className="container flex items-center justify-center py-12">
        <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Şifre Sıfırlama</CardTitle>
          <CardDescription>
            {tokenStatus === 'valid' && email
              ? `${email} hesabı için yeni şifre belirleyin.`
              : 'Bağlantı doğrulanıyor…'}
          </CardDescription>
        </CardHeader>
        {tokenStatus === 'checking' && (
          <CardContent className="text-muted-foreground text-sm">Kontrol ediliyor…</CardContent>
        )}
        {tokenStatus !== 'checking' && tokenStatus !== 'valid' && (
          <>
            <CardContent>
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {statusMsg[tokenStatus]}
              </div>
            </CardContent>
            <CardFooter>
              <Link to="/forgot-password" className="text-sm text-primary underline-offset-4 hover:underline">
                Yeni bağlantı iste
              </Link>
            </CardFooter>
          </>
        )}
        {tokenStatus === 'valid' && (
          <form onSubmit={onSubmit}>
            <CardContent className="space-y-4">
              {submitState === 'done' ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  Şifreniz değiştirildi. Giriş sayfasına yönlendiriliyorsunuz…
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="password">Yeni şifre</Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm">Yeni şifre (tekrar)</Label>
                    <Input
                      id="confirm"
                      type="password"
                      required
                      minLength={8}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                    />
                  </div>
                  {error && <div className="text-sm text-destructive">{error}</div>}
                </>
              )}
            </CardContent>
            {submitState !== 'done' && (
              <CardFooter>
                <Button type="submit" className="w-full" disabled={submitState === 'submitting'}>
                  {submitState === 'submitting' ? 'Kaydediliyor…' : 'Şifreyi Değiştir'}
                </Button>
              </CardFooter>
            )}
          </form>
        )}
        </Card>
      </div>
    </PublicShell>
  );
}
